// Matches a descriptor to a platform product by name, or by its size and the words its name adds,
// disambiguated by properties (grade/species/type/…), and resolves length values against a
// product's catalog lengths. Operates on the catalog injected via setCatalog().

import { inchesEqual, parseLengthToInches } from '~/utils/lengthParser';
import { diceCoefficient } from './similarity';
import { PACK_SIZE } from './stockCounts';
import type { Candidate, CatalogProduct, Resolution } from './types';

let activeCatalog: CatalogProduct[] = [];
let byId = new Map<string, CatalogProduct>();
// Species vocabulary derived from the loaded catalog (uppercased, longest-first) so descriptor
// parsing recognises exactly the species that actually exist — no hardcoded list to drift.
let knownSpecies: string[] = [];
let knownSpeciesWords: { species: string; words: string[] }[] = [];

interface ProductShape {
  product: CatalogProduct;
  species: string | null;
  words: Set<string>;
}

let shapesBySize = new Map<string, ProductShape[]>();
let catalogWords = new Set<string>();

// Memoize resolution by name and agreed species — at scale names repeat heavily, turning
// O(rows × catalog) fuzzy scoring into O(distinct names × catalog).
const resolveCache = new Map<string, Resolution>();

const catalogListeners = new Set<() => void>();

export function setCatalog(products: CatalogProduct[]): void {
  activeCatalog = products;
  byId = new Map(products.map((p) => [p.id, p]));
  const species = new Set<string>();
  for (const p of products) {
    const value = asString(p.properties.species);
    if (value) species.add(value.toUpperCase());
  }
  knownSpecies = Array.from(species).sort((a, b) => b.length - a.length);
  knownSpeciesWords = knownSpecies.map((value) => ({
    species: value,
    words: wordsOf(value),
  }));
  indexShapes(products);
  resolveCache.clear();
  catalogListeners.forEach((listener) => listener());
}

export function getCatalog(): CatalogProduct[] {
  return activeCatalog;
}

export function subscribeCatalog(listener: () => void): () => void {
  catalogListeners.add(listener);
  return () => catalogListeners.delete(listener);
}

export function speciesOf(product: CatalogProduct | null): string | null {
  return asString(product?.properties.species)?.toUpperCase() ?? null;
}

const MEASURE = String.raw`\d+-\d+\/\d+|\d+\/\d+|\d+(?:\.\d+)?`;
const SIZE = new RegExp(`(${MEASURE})\\s*[xX]\\s*(${MEASURE})`);

function inchesOf(value: string): number {
  return value.split('-').reduce((sum, part) => {
    const [top, bottom] = part.split('/');
    return sum + (bottom ? Number(top) / Number(bottom) : Number(top));
  }, 0);
}

const sizeKey = (thickness: number, width: number) => `${thickness}x${width}`;

function tokensOf(text: string): string[] {
  return text
    .replace(/\s*&\s*/g, '&')
    .split(/\s+/)
    .filter(Boolean);
}

const wordsOf = (text: string) => tokensOf(text.toUpperCase());

function sizedWordsOf(text: string): { size: string; words: string[] } | null {
  const size = SIZE.exec(text);
  if (!size) return null;
  const rest = `${text.slice(0, size.index)} ${text.slice(size.index + size[0].length)}`;
  return {
    size: sizeKey(inchesOf(size[1]), inchesOf(size[2])),
    words: wordsOf(rest),
  };
}

function indexShapes(products: CatalogProduct[]): void {
  shapesBySize = new Map();
  catalogWords = new Set();
  for (const product of products) {
    const named = sizedWordsOf(product.name);
    named?.words.forEach((word) => catalogWords.add(word));
    if (!named || product.thickness === null || product.width === null) {
      continue;
    }
    const species = speciesOf(product);
    const speciesWords = new Set(species ? wordsOf(species) : []);
    const key = sizeKey(product.thickness, product.width);
    const shapes = shapesBySize.get(key) ?? [];
    shapes.push({
      product,
      species,
      words: new Set(named.words.filter((word) => !speciesWords.has(word))),
    });
    shapesBySize.set(key, shapes);
  }
}

export function productById(id: string | null): CatalogProduct | null {
  return id ? (byId.get(id) ?? null) : null;
}

/** Display label for a chosen product id (its name). */
export function productLabel(id: string | null): string {
  return productById(id)?.name ?? '';
}

/** Lower-cased words of a name or a search, with a size as one word: "2 X 4" reads as "2x4". */
export function searchWordsOf(text: string): string[] {
  return tokensOf(
    text.toLowerCase().replace(new RegExp(SIZE.source, 'g'), '$1x$2')
  );
}

const wordsByProduct = new WeakMap<CatalogProduct, string[]>();

function productWordsOf(product: CatalogProduct): string[] {
  const cached = wordsByProduct.get(product);
  if (cached) return cached;
  const words = searchWordsOf(
    `${product.searchName} ${product.propertySummary}`
  );
  wordsByProduct.set(product, words);
  return words;
}

/** Whether each searched word starts a word of the product's name or properties, in any order. */
export function namesEveryWord(
  product: CatalogProduct,
  searched: string[]
): boolean {
  const words = productWordsOf(product);
  return searched.every((word) => words.some((own) => own.startsWith(word)));
}

function asString(value: unknown): string | null {
  if (typeof value === 'string' && value.trim()) return value.trim();
  if (Array.isArray(value) && value.length > 0)
    return value.map(String).join('/');
  return null;
}

/** Lumber-style grades read as "#2"; prefix a leading-numeric grade with "#", mirroring the
 * product detail display. Non-numeric grades (e.g. "SELECT") are shown as-is. */
function formatGrade(value: string): string {
  return /^\d/.test(value) && !value.startsWith('#') ? `#${value}` : value;
}

/**
 * Build the picker's second-line summary from exactly the attributes the product detail panel
 * shows, in the same order: Species · Grade · Category · Thickness · Width · Finish. Only the
 * values present on a product are rendered.
 */
export function buildPropertySummary(
  category: string,
  properties: Record<string, unknown>,
  actualThickness: number | null,
  actualWidth: number | null,
  finish: string | null
): string {
  const parts: string[] = [];
  const species = asString(properties.species);
  const grade = asString(properties.grade);
  if (species) parts.push(species);
  if (grade) parts.push(formatGrade(grade));
  if (category) parts.push(category);
  if (actualThickness !== null) parts.push(`${actualThickness}"`);
  if (actualWidth !== null) parts.push(`${actualWidth}"`);
  if (finish) parts.push(finish);
  return parts.join(' · ');
}

interface ParsedDescriptor {
  thickness: number | null;
  width: number | null;
  grade: string | null;
  species: string | null;
  raw: string;
}

function parseDescriptor(raw: string): ParsedDescriptor {
  const text = raw.trim();
  const result: ParsedDescriptor = {
    thickness: null,
    width: null,
    grade: null,
    species: null,
    raw: text,
  };
  if (!text) return result;

  const cross = SIZE.exec(text);
  if (cross) {
    result.thickness = inchesOf(cross[1]);
    result.width = inchesOf(cross[2]);
  }
  const grade = text.match(
    /#\s*\d+|\bselect\b|\bstd\b|\bstandard\b|\bprime\b/i
  );
  if (grade) result.grade = grade[0].replace(/\s+/g, '').toUpperCase();
  const upper = text.toUpperCase();
  result.species = knownSpecies.find((s) => upper.includes(s)) ?? null;
  return result;
}

const compact = (s: string) => s.toLowerCase().replace(/\s+/g, '');

function nameScore(
  rawLower: string,
  rawCompact: string,
  product: CatalogProduct
): number {
  return Math.max(
    diceCoefficient(rawLower, product.searchName),
    diceCoefficient(rawCompact, compact(product.name))
  );
}

/** Score a single product against a parsed descriptor (name-primary + property nudges). */
function scoreMatch(
  parsed: ParsedDescriptor,
  rawLower: string,
  rawCompact: string,
  product: CatalogProduct
): number {
  let score = nameScore(rawLower, rawCompact, product);
  if (parsed.thickness !== null && product.thickness === parsed.thickness)
    score += 0.03;
  if (parsed.width !== null && product.width === parsed.width) score += 0.03;
  const grade = asString(product.properties.grade)?.toUpperCase();
  const species = asString(product.properties.species)?.toUpperCase();
  if (parsed.grade && grade && grade.replace(/\s+/g, '') === parsed.grade)
    score += 0.05;
  if (parsed.species && species && species.includes(parsed.species))
    score += 0.05;
  return Math.min(1, score);
}

/**
 * Rank catalog candidates for a free-text product descriptor. `documentSpecies`, the species the
 * rest of the import agreed on, settles a descriptor that states none.
 */
export function resolveDescriptor(
  rawDescriptor: string,
  documentSpecies: string | null = null
): Resolution {
  const key = (rawDescriptor ?? '').trim();
  const cacheKey = `${documentSpecies ?? ''}|${key}`;
  const cached = resolveCache.get(cacheKey);
  if (cached) return cached;
  const result = computeResolution(key, documentSpecies);
  resolveCache.set(cacheKey, result);
  return result;
}

const MAX_CANDIDATES = 8;

function computeResolution(
  rawDescriptor: string,
  documentSpecies: string | null
): Resolution {
  const parsed = parseDescriptor(rawDescriptor.replace(PACK_SIZE, ''));
  if (!parsed.raw) return { status: 'none', candidates: [], chosenId: null };

  const rawLower = parsed.raw.toLowerCase();
  const rawCompact = compact(parsed.raw);
  const score = (product: CatalogProduct) =>
    scoreMatch(parsed, rawLower, rawCompact, product);

  const candidates: Candidate[] = [];
  for (const product of getCatalog()) {
    const productScore = score(product);
    if (productScore > 0.15) candidates.push({ product, score: productScore });
  }
  candidates.sort((a, b) => b.score - a.score);
  const trimmed = candidates.slice(0, MAX_CANDIDATES);

  // An exact normalized-name match (ignoring spaces and case) is a definitive match — e.g.
  // "2x10 #2 SYP" ↔ "2 X 10 #2 SYP". Auto-accept when exactly one product matches; if several
  // share the same normalized name (true duplicates), fall through so the user picks.
  const named = (name: string) =>
    candidates.filter((c) => compact(c.product.name) === name);
  const exactMatches =
    [compact(rawDescriptor), rawCompact]
      .map(named)
      .find((matches) => matches.length > 0) ?? [];
  if (exactMatches.length === 1) {
    return {
      status: 'auto',
      candidates: withFirst(trimmed, exactMatches[0].product),
      chosenId: exactMatches[0].product.id,
    };
  }

  const shaped = resolveByShape(parsed.raw, candidates, score, documentSpecies);
  if (shaped) return shaped;
  if (candidates.length === 0)
    return { status: 'none', candidates: [], chosenId: null };

  const top = candidates[0];
  const margin = top.score - (candidates[1]?.score ?? 0);

  // Auto-accept only a clear, unambiguous winner; otherwise the user picks (and sees properties).
  if (top.score >= 0.7 && margin >= 0.12) {
    return { status: 'auto', candidates: trimmed, chosenId: top.product.id };
  }
  if (top.score >= 0.3) {
    return { status: 'ambiguous', candidates: trimmed, chosenId: null };
  }
  return { status: 'none', candidates: trimmed, chosenId: null };
}

function resolveByShape(
  descriptor: string,
  ranked: Candidate[],
  score: (product: CatalogProduct) => number,
  documentSpecies: string | null
): Resolution | null {
  const named = sizedWordsOf(descriptor);
  if (!named) return null;
  const present = new Set(named.words);
  const statedSpecies = knownSpeciesWords.find(({ words }) =>
    words.every((word) => present.has(word))
  );
  const stated = statedSpecies?.species ?? null;
  const speciesWords = new Set(statedSpecies?.words);
  const others = named.words.filter((word) => !speciesWords.has(word));
  const words = new Set(others.filter((word) => catalogWords.has(word)));
  const ignored = others.filter((word) => !catalogWords.has(word));
  const fits = (shapesBySize.get(named.size) ?? []).filter(
    (shape) =>
      sameWords(shape.words, words) &&
      (stated === null || shape.species === stated)
  );
  if (fits.length === 0) return null;

  const fitting = new Set(fits.map((shape) => shape.product.id));
  const first = fits
    .map((shape) => ({ product: shape.product, score: score(shape.product) }))
    .sort((a, b) => b.score - a.score);
  const candidates = [
    ...first,
    ...ranked.filter((candidate) => !fitting.has(candidate.product.id)),
  ].slice(0, Math.max(MAX_CANDIDATES, first.length));

  const pick = pickAmong(fits, stated, documentSpecies);
  const top = [...first, ...ranked].reduce((best, candidate) =>
    candidate.score > best.score ? candidate : best
  ).product;
  if (ignored.length > 0 && pick?.shape.product !== top) return null;
  if (!pick) return { status: 'ambiguous', candidates, chosenId: null };

  const chosen = pick.shape.product;
  return {
    status: 'auto',
    candidates: withFirst(candidates, chosen),
    chosenId: chosen.id,
    assumedSpecies: pick.assumedSpecies,
    ignoredWords: ignored.length > 0 ? ignored : undefined,
  };
}

function pickAmong(
  fits: ProductShape[],
  stated: string | null,
  documentSpecies: string | null
): { shape: ProductShape; assumedSpecies?: string } | null {
  if (fits.length === 1) {
    const only = fits[0];
    return {
      shape: only,
      assumedSpecies: stated === null ? (only.species ?? undefined) : undefined,
    };
  }
  if (stated !== null || documentSpecies === null) return null;
  const ofDocumentSpecies = fits.filter(
    (shape) => shape.species === documentSpecies
  );
  return ofDocumentSpecies.length === 1
    ? { shape: ofDocumentSpecies[0], assumedSpecies: documentSpecies }
    : null;
}

function withFirst(
  candidates: Candidate[],
  product: CatalogProduct
): Candidate[] {
  return [
    ...candidates.filter((candidate) => candidate.product === product),
    ...candidates.filter((candidate) => candidate.product !== product),
  ];
}

function sameWords(a: Set<string>, b: Set<string>): boolean {
  return a.size === b.size && [...a].every((word) => b.has(word));
}

/**
 * Resolve a raw length string against a product to a canonical inches value, or '' if it doesn't
 * resolve. Matches the product's available lengths when known; if the product exposes no lengths
 * we accept any parseable length (the backend validates/creates the pairing on ingest).
 */
export function resolveLengthValue(
  productId: string,
  rawLength: string
): string {
  const product = productById(productId);
  if (!product) return '';
  const inches = parseLengthToInches(rawLength);
  if (inches === null) return '';
  // Products with no catalog lengths accept any parseable length, but it must still be positive
  // (0/negative would pass client validation and only fail late on the backend).
  if (product.lengths.length === 0) return inches > 0 ? String(inches) : '';
  const match = product.lengths.find((l) =>
    inchesEqual(l.actualLengthInches, inches)
  );
  return match ? String(match.actualLengthInches) : '';
}
